import { Route } from "@angular/router";
import { TransactionTypeEnum } from "./models/transaction";
import stateOfAssetsRoutes from "./state-of-assets-routes";

export const AccountingPaths = {
  Start: "start",
  PeppolStart: "peppol-start",
  Transactions: "verrichtingen",
  ReceiptsAndExpenses: "ontvangsten-uitgaven",
  Receipts: "ontvangsten",
  Expenses: "uitgaven",
  ClaimsAndDebts: "vorderingen-schulden",
  Claims: "vorderingen",
  Debts: "schulden",
  DayReceipts: "dagontvangsten",
  SaleInvoices: "verkoopfacturen",
  PurchaseInvoices: "aankoopfacturen",
  ReceivedCreditNotes: "ontvangen-creditnotas",
  GivenCreditNotes: "uitgereikte-creditnotas",
  TransactionAdd: "inboeken",
  TransactionAddForWorkYear: (workYearId: number | string) => `${workYearId}/inboeken`,
  TransactionAddForWorkYearAndType: (workYearId: number | string, typeId: number | string) =>
    `${workYearId}/inboeken/${typeId}`,
  TransactionAddForType: (typeId: number | string) => `inboeken/${typeId}`,
  TransactionAddForTypePurchaseInvoice: (typeId: number | string) =>
    `inboeken/${typeId}/aankoopfactuur`,
  TransactionAddForTypeSaleInvoice: (typeId: number | string) =>
    `inboeken/${typeId}/verkoopfactuur`,
  AankoopfactuurAddForPeppol: (fromPeppolDocumentId: number | string) =>
    `aankoopfactuur/inboeken/peppol/${fromPeppolDocumentId}`,
  OntvangenCreditnotaAddForPeppol: (fromPeppolDocumentId: number | string) =>
    `ontvangen-creditnota/inboeken/peppol/${fromPeppolDocumentId}`,
  TransactionEdit: (transactionId: number | string) => `bewerken/${transactionId}`,
  PeppolInbox: "peppolinbox",
  PeppolRequestList: "peppol-aanvragen",
  PeppolRequestAdd: "peppol-aanvraag/nieuw",
  PeppolRequestEdit: (requestId: number | string) => `peppol-aanvraag/${requestId}`,

  // Type-specific edit paths
  SchuldEdit: (id: number | string) => `schuld/bewerken/${id}`,
  SchuldAdd: "schuld/inboeken",
  AankoopfactuurEdit: (id: number | string) => `aankoopfactuur/bewerken/${id}`,
  AankoopfactuurAdd: "aankoopfactuur/inboeken",
  VorderingEdit: (id: number | string) => `vordering/bewerken/${id}`,
  VorderingAdd: "vordering/inboeken",
  VerkoopfactuurEdit: (id: number | string) => `verkoopfactuur/bewerken/${id}`,
  VerkoopfactuurAdd: "verkoopfactuur/inboeken",
  OntvangstEdit: (id: number | string) => `ontvangst/bewerken/${id}`,
  OntvangstAdd: "ontvangst/inboeken",
  UitgaveEdit: (id: number | string) => `uitgave/bewerken/${id}`,
  UitgaveAdd: "uitgave/inboeken",
  UitgereikteCreditnotaEdit: (id: number | string) => `uitgereikte-creditnota/bewerken/${id}`,
  UitgereikteCreditnotaAdd: "uitgereikte-creditnota/inboeken",
  OntvangenCreditnotaEdit: (id: number | string) => `ontvangen-creditnota/bewerken/${id}`,
  OntvangenCreditnotaAdd: "ontvangen-creditnota/inboeken",
  DagontvangstEdit: (id: number | string) => `dagontvangst/bewerken/${id}`,
  DagontvangstAdd: "dagontvangst/inboeken",
  GeldoverdrachtEdit: (id: number | string) => `geldoverdracht/bewerken/${id}`,
  GeldoverdrachtAdd: "geldoverdracht/inboeken",

  TransferAdd: "geldoverdracht",
  Import: "importeren",
  FinancialStatus: "status",
  Reports: "rapporten",
  AccountList: "rekeningenstelsel",
  AccountEdit: (accountId: number | string) => `rekeningenstelsel/${accountId}`,
  VatTypeSettings: "btw-instellingen",
  InvoiceSettings: "factuur-instellingen",
  FinancialPosts: "geldrekeningen",
  VatDeclration: "btwaangifte",
  Budgets: "begroting",
  BudgetAdd: "begroting/toevoegen",
  BudgetEdit: (budgetId: number | string) => `begroting/bewerken/${budgetId}`,
  BudgetView: (budgetId: number | string) => `begroting/${budgetId}/status`,
  StateOfAssets: "staat-van-vermogen",
  ClientListing: "klantenlisting",
};

export default [
  {
    path: AccountingPaths.Start,
    loadComponent: () => import("./pages/start/start.component").then((c) => c.StartComponent),
    data: {
      breadcrumb: "Start",
      title: "Boekhouding",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.PeppolStart,
    loadComponent: () =>
      import("./pages/peppol-start/peppol-start.component").then((c) => c.PeppolStartComponent),
    data: {
      breadcrumb: "Peppol activeren",
      title: "Peppol activeren",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.Transactions,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Ontvangsten en uitgaven zoeken",
      title: "Ontvangsten en uitgaven zoeken",
      subscriptionTypes: [1, 4],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.ontvangst, TransactionTypeEnum.uitgave],
    },
  },
  {
    path: "list",
    redirectTo: AccountingPaths.Transactions,
  },
  {
    path: AccountingPaths.ReceiptsAndExpenses,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Ontvangsten en uitgaven zoeken",
      title: "Ontvangsten en uitgaven zoeken",
      subscriptionTypes: [1, 4],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.ontvangst, TransactionTypeEnum.uitgave],
    },
  },
  {
    path: AccountingPaths.Receipts,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Ontvangsten zoeken",
      title: "Ontvangsten zoeken",
      subscriptionTypes: [1, 4],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.ontvangst],
    },
  },
  {
    path: AccountingPaths.Expenses,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Uitgaven zoeken",
      title: "Uitgaven zoeken",
      subscriptionTypes: [1, 4],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.uitgave],
    },
  },
  {
    path: AccountingPaths.ClaimsAndDebts,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Vorderingen en schulden zoeken",
      title: "Vorderingen en schulden zoeken",
      subscriptionTypes: [1, 4],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.schuld, TransactionTypeEnum.vordering],
    },
  },
  {
    path: AccountingPaths.Claims,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Vorderingen zoeken",
      title: "Vorderingen zoeken",
      subscriptionTypes: [1, 4, 5],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.vordering],
    },
  },
  {
    path: AccountingPaths.Debts,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Schulden zoeken",
      title: "Schulden zoeken",
      subscriptionTypes: [1, 4, 5],
      isVat: false,
      transactionTypes: [TransactionTypeEnum.schuld],
    },
  },
  {
    path: AccountingPaths.DayReceipts,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Dagontvangstenboek",
      title: "Dagontvangstenboek",
      subscriptionTypes: [1, 4],
      isVat: true,
      transactionTypes: [TransactionTypeEnum.dagOntvangsten],
    },
  },
  {
    path: AccountingPaths.SaleInvoices,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Verkoopfacturen",
      title: "Verkoopfacturen",
      subscriptionTypes: [1, 4, 5],
      isVat: true,
      transactionTypes: [TransactionTypeEnum.vordering],
    },
  },
  {
    path: AccountingPaths.PurchaseInvoices,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Aankoopfacturen",
      title: "Aankoopfacturen",
      subscriptionTypes: [1, 4, 5],
      isVat: true,
      transactionTypes: [TransactionTypeEnum.schuld],
    },
  },
  {
    path: AccountingPaths.ReceivedCreditNotes,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Ontvangen creditnota's",
      title: "Ontvangen creditnota's",
      subscriptionTypes: [1, 4, 5],
      isVat: true,
      transactionTypes: [TransactionTypeEnum.ontvangenCreditnota],
    },
  },
  {
    path: AccountingPaths.GivenCreditNotes,
    loadComponent: () =>
      import("./pages/transactions-list/transactions-list.component").then(
        (c) => c.TransactionsListComponent
      ),
    data: {
      breadcrumb: "Uitgereikte creditnota's",
      title: "Uitgereikte creditnota's",
      subscriptionTypes: [1, 4, 5],
      isVat: true,
      transactionTypes: [TransactionTypeEnum.uitgereikteCreditnota],
    },
  },
  // Type-specific edit routes
  {
    path: AccountingPaths.SchuldEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/schuld-edit/schuld-edit.component").then(
        (c) => c.SchuldEditComponent
      ),
    data: {
      breadcrumb: "Schuld bewerken",
      title: "Schuld bewerken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.SchuldAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/schuld-edit/schuld-edit.component").then(
        (c) => c.SchuldEditComponent
      ),
    data: {
      breadcrumb: "Schuld inboeken",
      title: "Nieuwe schuld inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.AankoopfactuurEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/aankoopfactuur-edit/aankoopfactuur-edit.component").then(
        (c) => c.AankoopfactuurEditComponent
      ),
    data: {
      breadcrumb: "Aankoopfactuur bewerken",
      title: "Aankoopfactuur bewerken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.AankoopfactuurAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/aankoopfactuur-edit/aankoopfactuur-edit.component").then(
        (c) => c.AankoopfactuurEditComponent
      ),
    data: {
      breadcrumb: "Aankoopfactuur inboeken",
      title: "Nieuwe aankoopfactuur inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.VorderingEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/vordering-edit/vordering-edit.component").then(
        (c) => c.VorderingEditComponent
      ),
    data: {
      breadcrumb: "Vordering bewerken",
      title: "Vordering bewerken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.VorderingAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/vordering-edit/vordering-edit.component").then(
        (c) => c.VorderingEditComponent
      ),
    data: {
      breadcrumb: "Vordering inboeken",
      title: "Nieuwe vordering inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.VerkoopfactuurEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/verkoopfactuur-edit/verkoopfactuur-edit.component").then(
        (c) => c.VerkoopfactuurEditComponent
      ),
    data: {
      breadcrumb: "Verkoopfactuur bewerken",
      title: "Verkoopfactuur bewerken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.VerkoopfactuurAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/verkoopfactuur-edit/verkoopfactuur-edit.component").then(
        (c) => c.VerkoopfactuurEditComponent
      ),
    data: {
      breadcrumb: "Verkoopfactuur inboeken",
      title: "Nieuwe verkoopfactuur inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.OntvangstEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/ontvangst-edit/ontvangst-edit.component").then(
        (c) => c.OntvangstEditComponent
      ),
    data: {
      breadcrumb: "Ontvangst bewerken",
      title: "Ontvangst bewerken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.OntvangstAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/ontvangst-edit/ontvangst-edit.component").then(
        (c) => c.OntvangstEditComponent
      ),
    data: {
      breadcrumb: "Ontvangst inboeken",
      title: "Nieuwe ontvangst inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.UitgaveEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/uitgave-edit/uitgave-edit.component").then(
        (c) => c.UitgaveEditComponent
      ),
    data: {
      breadcrumb: "Uitgave bewerken",
      title: "Uitgave bewerken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.UitgaveAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/uitgave-edit/uitgave-edit.component").then(
        (c) => c.UitgaveEditComponent
      ),
    data: {
      breadcrumb: "Uitgave inboeken",
      title: "Nieuwe uitgave inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.UitgereikteCreditnotaEdit(":id"),
    loadComponent: () =>
      import(
        "./pages/transaction-edit/uitgereikte-creditnota-edit/uitgereikte-creditnota-edit.component"
      ).then((c) => c.UitgereikteCreditnotaEditComponent),
    data: {
      breadcrumb: "Uitgereikte creditnota bewerken",
      title: "Uitgereikte creditnota bewerken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.UitgereikteCreditnotaAdd,
    loadComponent: () =>
      import(
        "./pages/transaction-edit/uitgereikte-creditnota-edit/uitgereikte-creditnota-edit.component"
      ).then((c) => c.UitgereikteCreditnotaEditComponent),
    data: {
      breadcrumb: "Uitgereikte creditnota inboeken",
      title: "Nieuwe uitgereikte creditnota inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.OntvangenCreditnotaEdit(":id"),
    loadComponent: () =>
      import(
        "./pages/transaction-edit/ontvangen-creditnota-edit/ontvangen-creditnota-edit.component"
      ).then((c) => c.OntvangenCreditnotaEditComponent),
    data: {
      breadcrumb: "Ontvangen creditnota bewerken",
      title: "Ontvangen creditnota bewerken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.OntvangenCreditnotaAdd,
    loadComponent: () =>
      import(
        "./pages/transaction-edit/ontvangen-creditnota-edit/ontvangen-creditnota-edit.component"
      ).then((c) => c.OntvangenCreditnotaEditComponent),
    data: {
      breadcrumb: "Ontvangen creditnota inboeken",
      title: "Nieuwe ontvangen creditnota inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.DagontvangstEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/dagontvangst-edit/dagontvangst-edit.component").then(
        (c) => c.DagontvangstEditComponent
      ),
    data: {
      breadcrumb: "Dagontvangst bewerken",
      title: "Dagontvangst bewerken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.DagontvangstAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/dagontvangst-edit/dagontvangst-edit.component").then(
        (c) => c.DagontvangstEditComponent
      ),
    data: {
      breadcrumb: "Dagontvangst inboeken",
      title: "Nieuwe dagontvangst inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.GeldoverdrachtEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/geldoverdracht-edit/geldoverdracht-edit.component").then(
        (c) => c.GeldoverdrachtEditComponent
      ),
    data: {
      breadcrumb: "Geldoverdracht bewerken",
      title: "Geldoverdracht bewerken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.GeldoverdrachtAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/geldoverdracht-edit/geldoverdracht-edit.component").then(
        (c) => c.GeldoverdrachtEditComponent
      ),
    data: {
      breadcrumb: "Geldoverdracht inboeken",
      title: "Nieuwe geldoverdracht inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.TransactionAddForWorkYear(":workingyearId"),
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit.component").then(
        (c) => c.TransactionEditComponent
      ),
    data: {
      breadcrumb: "Verrichting inboeken",
      title: "Nieuwe verrichting inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.TransactionAddForWorkYearAndType(":workingyearId", ":typeId"),
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit.component").then(
        (c) => c.TransactionEditComponent
      ),
    data: {
      breadcrumb: "Verrichting inboeken",
      title: "Nieuwe verrichting inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.TransactionAdd,
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit.component").then(
        (c) => c.TransactionEditComponent
      ),
    data: {
      breadcrumb: "Verrichting inboeken",
      title: "Nieuwe verrichting inboeken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.TransactionAddForType(":typeId"),
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit.component").then(
        (c) => c.TransactionEditComponent
      ),
    data: {
      breadcrumb: "Verrichting inboeken",
      title: "Nieuwe verrichting inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.TransactionAddForTypePurchaseInvoice(":typeId"),
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit.component").then(
        (c) => c.TransactionEditComponent
      ),
    data: {
      breadcrumb: "Aankoopfactuur inboeken",
      title: "Nieuwe aankoopfactuur inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.TransactionAddForTypeSaleInvoice(":typeId"),
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit.component").then(
        (c) => c.TransactionEditComponent
      ),
    data: {
      breadcrumb: "Verkoopfactuur inboeken",
      title: "Nieuwe verkoopfactuur inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.TransactionEdit(":id"),
    loadComponent: () =>
      import("./pages/transaction-edit/transaction-edit-router.component").then(
        (c) => c.TransactionEditRouterComponent
      ),
    data: {
      breadcrumb: "Verrichting bewerken",
      title: "",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.TransferAdd,
    loadComponent: () =>
      import("./pages/transfer-add/transfer-add.component").then((c) => c.TransferAddComponent),
    data: {
      breadcrumb: "Geldoverdracht inboeken",
      title: "Nieuwe geldoverdracht inboeken",
    },
  },
  {
    path: AccountingPaths.PeppolInbox,
    loadComponent: () =>
      import("./pages/peppol-inbox/peppol-inbox.component").then((c) => c.PeppolInboxComponent),
    data: {
      breadcrumb: "Peppol-inbox",
      title: "Peppol-inbox",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.AankoopfactuurAddForPeppol(":fromPeppolDocumentId"),
    loadComponent: () =>
      import("./pages/transaction-edit/aankoopfactuur-edit/aankoopfactuur-edit.component").then(
        (c) => c.AankoopfactuurEditComponent
      ),
    data: {
      breadcrumb: "Aankoopfactuur inboeken",
      title: "Nieuwe aankoopfactuur inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.OntvangenCreditnotaAddForPeppol(":fromPeppolDocumentId"),
    loadComponent: () =>
      import(
        "./pages/transaction-edit/ontvangen-creditnota-edit/ontvangen-creditnota-edit.component"
      ).then((c) => c.OntvangenCreditnotaEditComponent),
    data: {
      breadcrumb: "Ontvangen creditnota inboeken",
      title: "Nieuwe ontvangen creditnota inboeken",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.PeppolRequestEdit(":id"),
    loadComponent: () =>
      import("./pages/peppol-request-edit/peppol-request-edit.component").then(
        (c) => c.PeppolRequestEditComponent
      ),
    data: {
      breadcrumb: "Peppol aanvraag bewerken",
      title: "Peppol aanvraag bewerken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.Import,
    loadComponent: () =>
      import("./pages/transaction-import/transaction-import.component").then(
        (c) => c.TransactionImportComponent
      ),
    data: {
      breadcrumb: "Verrichtingen importeren",
      title: "Verrichtingen importeren",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.FinancialStatus,
    loadComponent: () =>
      import("./pages/financial-status/financial-status.component").then(
        (c) => c.FinancialStatusComponent
      ),
    data: {
      breadcrumb: "Financiële status",
      title: "Financiële status",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.Reports,
    loadComponent: () =>
      import("./pages/financial-reports/financial-reports.component").then(
        (c) => c.FinancialReportsComponent
      ),
    data: {
      breadcrumb: "Financiële rapporten",
      title: "Financiële rapporten en verslagen",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: "reports",
    redirectTo: AccountingPaths.Reports,
  },
  {
    path: AccountingPaths.AccountList,
    loadComponent: () =>
      import("./pages/account-list/account-list.component").then((c) => c.AccountListComponent),
    data: {
      breadcrumb: "Rekeningenstelsel",
      title: "Ontvangsten- en uitgavenrekeningen",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.AccountEdit(":id"),
    loadComponent: () =>
      import("./pages/account-list/account-list.component").then((c) => c.AccountListComponent),
    data: {
      breadcrumb: "Rekeningenstelsel",
      title: "Ontvangsten- en uitgavenrekeningen",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.VatTypeSettings,
    loadComponent: () =>
      import("./pages/vat-type-edit/vat-type-edit.component").then((c) => c.VatTypeEditComponent),
    data: {
      breadcrumb: "Voorkeuren en instellingen",
      title: "Boekhouding",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.InvoiceSettings,
    loadComponent: () =>
      import("./pages/invoice-settings-edit/invoice-settings-edit.component").then(
        (c) => c.InvoiceSettingsEditComponent
      ),
    data: {
      breadcrumb: "Voorkeuren en instellingen",
      title: "Boekhouding",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.FinancialPosts,
    loadComponent: () =>
      import("./pages/financialpost-list/financialpost-list.component").then(
        (c) => c.FinancialpostListComponent
      ),
    data: {
      breadcrumb: "Geldrekeningen",
      title: "Geldrekeningen",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.VatDeclration,
    loadComponent: () =>
      import("./pages/vat-declaration/vat-declaration.component").then(
        (c) => c.VatDeclarationComponent
      ),
    data: {
      breadcrumb: "Btw-aangifte",
      title: "Btw-aangifte",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.Budgets,
    loadComponent: () =>
      import("./pages/budget-list/budget-list.component").then((c) => c.BudgetListComponent),
    data: {
      breadcrumb: "Begroting",
      title: "Begroting",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.BudgetAdd,
    loadComponent: () =>
      import("./pages/budget-edit/budget-edit.component").then((c) => c.BudgetEditComponent),
    data: {
      breadcrumb: "Begroting toevoegen",
      title: "Begroting toevoegen",
      SubscriptionTypeEnum: [1, 4],
    },
  },
  {
    path: AccountingPaths.BudgetEdit(":id"),
    loadComponent: () =>
      import("./pages/budget-edit/budget-edit.component").then((c) => c.BudgetEditComponent),
    data: {
      breadcrumb: "Begroting bewerken",
      title: "Begroting bewerken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.BudgetView(":id"),
    loadComponent: () =>
      import("./pages/budget-view/budget-view.component").then((c) => c.BudgetViewComponent),
    data: {
      breadcrumb: "Begroting bekijken",
      title: "Begroting bekijken",
      subscriptionTypes: [1, 4],
    },
  },
  {
    path: AccountingPaths.StateOfAssets,
    data: { breadcrumb: "Staat van het Vermogen" },
    children: stateOfAssetsRoutes,
  },
  {
    path: AccountingPaths.PeppolRequestList,
    loadComponent: () =>
      import("./pages/peppol-request-list/peppol-request-list.component").then(
        (c) => c.PeppolRequestListComponent
      ),
    data: {
      breadcrumb: "Peppol Aanvragen",
      title: "Peppol Aanvragen",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.PeppolRequestAdd,
    loadComponent: () =>
      import("./pages/peppol-request-edit/peppol-request-edit.component").then(
        (c) => c.PeppolRequestEditComponent
      ),
    data: {
      breadcrumb: "Nieuwe Peppol aanvraag",
      title: "Nieuwe Peppol aanvraag",
      subscriptionTypes: [1, 4, 5],
    },
  },
  {
    path: AccountingPaths.ClientListing,
    loadComponent: () =>
      import("../menu/pages/on-hold/on-hold.component").then((c) => c.OnHoldComponent),
    data: {
      breadcrumb: "Klantenlisting",
      title: "Klantenlisting",
      previousVersionUrl: "https://app.assistonline.eu/dba/#/app/boekhouding/klantenlisting",
      subscriptionTypes: [1, 4],
    },
  },
] satisfies Route[];
